import { Navigate, useLocation } from "react-router-dom";
import { AppError, H1, Link, LoadingSpinner, Text, AllCaps } from "atoms";
import { useToggle } from "utils/hooks";
import { isRedirectState, type RedirectState } from "utils/routing";
import { useAuth } from "./AuthProvider";
import { AuthWrapper } from "./AuthWrapper";
import { ConnectWallet, type ConnectHandler } from "./ConnectWallet";
import { EmailSignIn } from "./EmailSignIn";

type SignInProps = {
  isCreateAccount?: boolean;
};

/** Custom hook to extract redirect state from router location */
function useRedirectState() {
  const location = useLocation();
  return isRedirectState(location.state) ? location.state : undefined;
}

/** Header content for account creation flow */
function CreateAccountHeader() {
  return (
    <>
      <H1 size="h3">Create an Account by Connecting Your&nbsp;Wallet</H1>
      <Text size="xs" textAlign="center">
        To leave reviews and get the most out of <AllCaps>Hug</AllCaps>
        you will need an account&nbsp;to&nbsp;continue.
      </Text>
    </>
  );
}

/** Header content for sign in flow */
function SignInHeader({ isWalletMethod }: { isWalletMethod: boolean }) {
  return (
    <>
      <H1 size="h3">
        {isWalletMethod
          ? "Sign In By Connecting Your Wallet"
          : "Sign In With Email"}
      </H1>
      <Text size="xs" textAlign="center">
        Sign in to start leaving reviews and leveling up
        for&nbsp;your&nbsp;contributions.
      </Text>
    </>
  );
}

/** Footer link to sign in (shown during account creation) */
function SignInLink({ redirectState }: { redirectState?: RedirectState }) {
  return (
    <Text size="xs" textAlign="center">
      Already have an account?{" "}
      <Link to="/sign-in" state={redirectState}>
        Sign In
      </Link>
    </Text>
  );
}

/** Footer link to create account (shown during sign in) */
function CreateAccountLink({
  redirectState,
}: {
  redirectState?: RedirectState;
}) {
  return (
    <Text size="xs" textAlign="center">
      Don&apos;t have an account?{" "}
      <Link to="/register" state={redirectState}>
        Create an account
      </Link>
    </Text>
  );
}

function SignIn({ isCreateAccount = false }: SignInProps) {
  const location = useLocation();
  const redirectState = useRedirectState();
  const [isWalletMethod, toggleMethod] = useToggle(isCreateAccount);

  const { signIn, error, isError, isLoading, user, roles } = useAuth();

  const handleConnect: ConnectHandler = (connection) => {
    signIn(connection);
  };

  // Redirect authenticated users
  if (user) {
    const needsOnboarding = !roles.isCollector && user.forceOnboarding;

    if (needsOnboarding) {
      return (
        <Navigate
          to="/onboarding"
          state={{ ...redirectState, isCreateAccount }}
          replace
        />
      );
    }

    return <Navigate to={redirectState?.from ?? "/"} replace />;
  }

  const showCreateAccountLink =
    !isCreateAccount &&
    isWalletMethod &&
    location.pathname !== "create-account";

  return (
    <AuthWrapper
      title="Connect Wallet"
      header={
        isCreateAccount ? (
          <CreateAccountHeader />
        ) : (
          <SignInHeader isWalletMethod={isWalletMethod} />
        )
      }
    >
      {isError && <AppError>{error}</AppError>}

      {isWalletMethod ? (
        <ConnectWallet
          showEmailSignIn={!isCreateAccount ? toggleMethod : undefined}
          onConnect={handleConnect}
        />
      ) : (
        <EmailSignIn toggleConnectMethod={toggleMethod} />
      )}

      {isLoading && <LoadingSpinner />}

      {isCreateAccount && isWalletMethod && (
        <SignInLink redirectState={redirectState} />
      )}

      {showCreateAccountLink && (
        <CreateAccountLink redirectState={redirectState} />
      )}
    </AuthWrapper>
  );
}

export { SignIn };
